/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* eslint-disable class-methods-use-this */
/* eslint-disable no-underscore-dangle */
import { providers, envUtil } from "dc-core";
import * as stores from "stores";
import { v4 as uuidv4 } from "uuid";
import { ORGANIZE_ERRORS } from "constants/enums";
import { getAssetURI } from "selectors/documentSelectors";

class OrganizeApisManager {
  constructor() {
    this.validIntents = [
      "organize-pdf",
      "delete-pages",
      "extract-pages",
      "insert-pdf",
      "reorder-pages",
      "rotate-pages",
      "number-pages"
    ];
    this.intent = null;
    this.nonce = null;
  }

  async validateIntent(intent) {
    if (!intent || !this.validIntents.includes(intent)) {
      throw new Error(ORGANIZE_ERRORS.INVALID_INTENT);
    }
  }

  async setupPluginIfRequired() {
    const editProvider = await providers["edit-provider"]();
    await editProvider.setupPluginIfRequired(getAssetURI(stores));
  }

  async addLicensingInfo(params) {
    const editProvider = await providers["edit-provider"]();
    const token = await editProvider.getAccessTokenForLicensing();
    const licensingInfo = {
      token,
      domain: window.location.origin
    };
    params.licensingInfo = licensingInfo;
  }

  async organizeWillBegin(params) {
    const { intent } = params;
    try {
      await this.validateIntent(intent);
      await this.addLicensingInfo(params);
      await this.setupPluginIfRequired();
      this.intent = intent;
    } catch (error) {
      throw error;
    }
  }

  static getDcApiClientId() {
    let clientId = "dc-local-virgoweb";
    if (envUtil.getHostEnv() === "prod") {
      clientId = "dc-prod-virgoweb";
    } else if (envUtil.getHostEnv() === "stage") {
      clientId = "dc-stage-virgoweb";
    } else if (envUtil.getHostEnv() === "dev") {
      clientId = "dc-dev-virgoweb";
    }

    return clientId;
  }

  async fetchToken() {
    const dcApiProvider = await providers.dcapi();
    const dcApi = dcApiProvider.getDcapi();
    const requestId = uuidv4();
    const dcApiClientId = OrganizeApisManager.getDcApiClientId();
    try {
      const res = await dcApi.call("assets.authorize_client_verb", {
        accept: "application/json",
        content_type: "authorize_client_verb_v1.json",
        headers: {
          "x-request-id": requestId,
          "X-Key-Pair-Version": "v1",
          "x-api-key": dcApiClientId
        },
        content: {
          nonce: this.nonce,
          domain: window.location.origin,
          intent: this.intent
        }
      });
      if (res && res.content && res.content.token) {
        return res.content.token;
      }
      return null;
    } catch (error) {
      const errorInfo = {};
      errorInfo.name = ORGANIZE_ERRORS.FETCH_TOKEN_FAILURE;
      errorInfo.message = error.message || ORGANIZE_ERRORS.UNKNOWN_ERROR;
      errorInfo.details = {
        "x-request-id": requestId
      };
      throw new Error(errorInfo);
    }
  }

  async organizeDidEnd(params) {
    params.token = await this.fetchToken();
    params.domain = window.location.origin;
    params.intent = this.intent;
  }

  static async markPDFEdited() {
    const editProvider = await providers["edit-provider"]();
    editProvider.getEditPlugin().setPdfIsEdited(true);
  }

  async organizeWillBeginResponseHandler(response) {
    OrganizeApisManager._checkIfError(response);
    if (response && response.data && response.data.nonce) {
      this.nonce = response.data.nonce;
      delete response.data.nonce;
    }
    return response;
  }

  static _checkIfError(response) {
    if (response && response.data && response.data.status === "failed") {
      throw new Error(response.data.error);
    }
  }

  async organizeDidEndResponseHandler(response, options) {
    OrganizeApisManager._checkIfError(response);
    await OrganizeApisManager.markPDFEdited(options);
    return response;
  }
}

export default new OrganizeApisManager();
